import { difference, keyBy } from 'es-toolkit';
import React, { useCallback, useMemo } from 'react';
import {
  CustomTreeData,
  Filter,
  FilteringState,
  IntegratedFiltering,
  IntegratedSelection,
  IntegratedSorting,
  SelectionState,
  SortingState,
  TreeDataState,
} from '@devexpress/dx-react-grid';
import {
  ColumnChooser,
  Grid,
  TableColumnResizing,
  TableColumnVisibility,
  TableFilterRow,
  TableHeaderRow,
  TableSelection,
  TableTreeColumn,
  Toolbar,
  VirtualTable,
} from '@devexpress/dx-react-grid-material-ui';
import { Paper, TableCell } from '@mui/material';
import { castDraft } from 'immer';

import { expandToFillParentStyles } from '@paragon/client-tools';
import { assertExhaustiveSwitch, naturalSort, naturalSortByProperty } from '@paragon/utilities';

import { loadComponentsForTruckload } from '../../../actions/componentActions';
import { getDistanceFormatterSelector } from '../../../selectors/getDistanceFormatterSelector';
import { getBundleRowId } from '../../../selectors/scheduleGroupGrid';
import {
  getBundleGuidFromRowId,
  getTruckloadGridRows,
  getTruckloadGuidFromRowId,
  isBundleRow,
  isProductionGroupRow,
  isTruckloadRow,
  TruckloadGridRow,
} from '../../../selectors/truckloadGrid';
import {
  checkRows,
  collapseRows,
  expandRows,
  setUserColumnWidths,
  uncheckRows,
} from '../../../store/truckloadGridSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { CustomTreeCell } from '../CustomTreeCell';
import { GridRoot } from '../GridRoot';
import { Column } from '../gridTypes';
import {
  compareDates,
  distancesToString,
  formatDateToString,
  lengthFilter,
  trimmedCaseInsensitiveFilter,
} from '../gridUtilities';
import { StatusesAsColorBar } from '../StatusesAsColorBar';
import { StatusesAsColoredText } from '../StatusesAsColoredText';
import { TruckloadGridRowActions } from './TruckloadGridRowActions';
import { TruckloadGridToolbarPlugin } from './TruckloadGridToolbarPlugin';

const statusColumnWidth = 170;

function rowSelectionDisabled(row: TruckloadGridRow) {
  const { type } = row;
  switch (type) {
    case 'truckload':
      return false;
    case 'productionGroup':
      return true;
    case 'bundle':
      return true;
    case 'groupedProductionComponentInstance':
      return true;
    default:
      assertExhaustiveSwitch(type);
  }
}

export function TruckloadGrid() {
  const dispatch = useAppDispatch();
  const rows = useAppSelector(getTruckloadGridRows);
  const checkedRows = useAppSelector((state) => state.truckloadGrid.checkedRows);
  const expandedRows = useAppSelector((state) => state.truckloadGrid.expandedRows);
  const userColumnWidths = useAppSelector((state) => state.truckloadGrid.userColumnWidths);
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);

  const rowsKeyedByRowId = useMemo(() => keyBy(rows, (row) => row.rowId), [rows]);

  const getDescendantRowIds = useCallback(
    (rowId: string): string[] => {
      if (isTruckloadRow(rowId)) {
        const productionGroupRowIds = rows
          .filter((row) => row.type === 'productionGroup' && row.truckloadRowId === rowId)
          .map((row) => row.rowId);
        const bundleRowIds = rows
          .filter((row) => row.type === 'bundle' && productionGroupRowIds.includes(row.productionGroupRowId))
          .map((row) => row.rowId);
        const bundleGuids = bundleRowIds.map(getBundleGuidFromRowId);
        const groupedProductionComponentInstanceRowIds = rows
          .filter((row) => row.type === 'groupedProductionComponentInstance' && bundleGuids.includes(row.bundleGuid))
          .map((row) => row.rowId);
        return productionGroupRowIds.concat(bundleRowIds).concat(groupedProductionComponentInstanceRowIds);
      }
      if (isProductionGroupRow(rowId)) {
        const bundleRowIds = rows
          .filter((row) => row.type === 'bundle' && row.productionGroupRowId === rowId)
          .map((row) => row.rowId);
        const bundleGuids = bundleRowIds.map(getBundleGuidFromRowId);
        const groupedProductionComponentInstanceRowIds = rows
          .filter((row) => row.type === 'groupedProductionComponentInstance' && bundleGuids.includes(row.bundleGuid))
          .map((row) => row.rowId);
        return bundleRowIds.concat(groupedProductionComponentInstanceRowIds);
      }
      if (isBundleRow(rowId)) {
        const bundleGuid = getBundleGuidFromRowId(rowId);
        const groupedProductionComponentInstanceRowIds = rows
          .filter((row) => row.type === 'groupedProductionComponentInstance' && row.bundleGuid === bundleGuid)
          .map((row) => row.rowId);
        return groupedProductionComponentInstanceRowIds;
      }

      return [];
    },
    [rows],
  );

  const getAncestorRowIds = useCallback(
    (rowId: string, rowsKeyedByRowId: { [rowId: string]: TruckloadGridRow }): string[] => {
      const row = rowsKeyedByRowId[rowId];
      if (row === undefined) throw new Error('Row not found');
      const { type } = row;
      switch (type) {
        case 'truckload':
          return [];
        case 'productionGroup':
          return [row.truckloadRowId];
        case 'bundle':
          const productionGroupRow = rowsKeyedByRowId[row.productionGroupRowId];
          if (!productionGroupRow || productionGroupRow.type !== 'productionGroup')
            throw new Error('Row not found or is wrong type');
          return [row.productionGroupRowId, productionGroupRow.truckloadRowId];
        case 'groupedProductionComponentInstance': {
          const bundleRowId = getBundleRowId(row.bundleGuid);
          const bundleRow = rowsKeyedByRowId[bundleRowId];
          if (!bundleRow || bundleRow.type !== 'bundle') throw new Error('Row not found or is wrong type');
          const productionGroupRow = rowsKeyedByRowId[bundleRow.productionGroupRowId];
          if (!productionGroupRow || productionGroupRow.type !== 'productionGroup')
            throw new Error('Row not found or is wrong type');
          return [bundleRowId, productionGroupRow.rowId, productionGroupRow.truckloadRowId];
        }
        default:
          assertExhaustiveSwitch(type);
      }
    },
    [],
  );

  const columns: readonly Column<TruckloadGridRow>[] = useMemo(() => {
    function trimmedCaseInsensitiveFilterForSelfAndHierarchyWithValueGetter(
      valueGetter: (row: TruckloadGridRow) => any,
    ) {
      return function trimmedCaseInsensitiveFilterForSelfAndHierarchy(
        value: any,
        filter: Filter,
        row: TruckloadGridRow,
      ) {
        const descendantRows = getDescendantRowIds(row.rowId).map((rowId) => rowsKeyedByRowId[rowId]);
        if (descendantRows.some((descendantRow) => trimmedCaseInsensitiveFilter(valueGetter(descendantRow), filter)))
          return true;

        const ancestorRows = getAncestorRowIds(row.rowId, rowsKeyedByRowId);
        if (
          ancestorRows.some((ancestorRowId) =>
            trimmedCaseInsensitiveFilter(valueGetter(rowsKeyedByRowId[ancestorRowId]), filter),
          )
        )
          return true;

        return trimmedCaseInsensitiveFilter(valueGetter(row), filter);
      };
    }

    return [
      {
        name: 'name',
        title: 'Name',
        defaultWidth: 350,
        sortingEnabled: true,
        filteringEnabled: true,
        filteringPredicate: trimmedCaseInsensitiveFilterForSelfAndHierarchyWithValueGetter((row) => row.name),
      },
      {
        name: 'actions',
        title: 'Actions',
        defaultWidth: 220,
        sortingEnabled: false,
        filteringEnabled: false,
        getCellValue: (row) => <TruckloadGridRowActions row={row} />,
      },
      {
        name: 'trailer',
        title: 'Trailer',
        defaultWidth: 100,
        sortingEnabled: true,
        filteringEnabled: false,
        getCellValue: (row) => (row.type === 'truckload' ? row.trailer : null),
      },
      {
        name: 'truckDriver',
        title: 'Truck Driver',
        defaultWidth: 150,
        sortingEnabled: true,
        filteringEnabled: false,
        getCellValue: (row) => (row.type === 'truckload' ? row.truckDriver : null),
      },
      {
        name: 'shipDate',
        title: 'Ship Date',
        sortingEnabled: true,
        sortingCompare: compareDates,
        filteringEnabled: false,
        defaultWidth: 120,
        getCellValue: (row) =>
          row.type === 'truckload' && row.shipDate !== null ? formatDateToString(row.shipDate) : null,
      },
      {
        name: 'trussCount',
        title: 'Truss Count',
        sortingEnabled: false,
        filteringEnabled: false,
        defaultWidth: 120,
        align: 'right',
        getCellValue: (row) => row.trussCount,
      },
      {
        name: 'boardFeet',
        title: 'BF',
        sortingEnabled: false,
        filteringEnabled: false,
        defaultWidth: 80,
        align: 'right',
        getCellValue: (row) => (row.boardFeet === null ? null : Math.round(row.boardFeet)),
      },
      {
        name: 'linearFeet',
        title: 'LF',
        sortingEnabled: false,
        filteringEnabled: false,
        defaultWidth: 80,
        align: 'right',
        getCellValue: (row) => (row.linearFeet === null ? null : Math.round(row.linearFeet)),
      },
      {
        name: 'thicknesses',
        title: 'Thickness',
        defaultWidth: 110,
        align: 'right',
        sortingEnabled: true,
        sortingCompare: naturalSort,
        filteringEnabled: true,
        filteringPredicate: lengthFilter,
        getCellValue: (row) => distancesToString(row.thicknesses, distanceFormatter),
      },
      {
        name: 'heights',
        title: 'Height',
        sortingEnabled: true,
        sortingCompare: naturalSort,
        filteringEnabled: true,
        defaultWidth: 90,
        align: 'right',
        filteringPredicate: lengthFilter,
        getCellValue: (row) => distancesToString(row.heights, distanceFormatter),
      },
      {
        name: 'widths',
        title: 'Width',
        defaultWidth: 90,
        align: 'right',
        sortingEnabled: true,
        sortingCompare: naturalSort,
        filteringEnabled: true,
        filteringPredicate: lengthFilter,
        getCellValue: (row) => distancesToString(row.widths, distanceFormatter),
      },
      {
        name: 'status',
        title: 'Status',
        defaultWidth: statusColumnWidth,
        sortingEnabled: false,
        filteringEnabled: false,
        getCellValue: (row) =>
          row.type === 'truckload' ? (
            <StatusesAsColorBar
              statusPercentages={row.statusPercentages}
              statusColumnWidth={statusColumnWidth}
              rowId={row.rowId}
            />
          ) : (
            <StatusesAsColoredText statusPercentages={row.statusPercentages} rowId={row.rowId} />
          ),
      },
    ];
  }, [getAncestorRowIds, getDescendantRowIds, rowsKeyedByRowId, distanceFormatter]);
  const columnSortingExtensions = useMemo(
    () =>
      columns.map((column) => ({
        columnName: column.name,
        sortingEnabled: column.sortingEnabled,
        compare: column.sortingCompare,
      })),
    [columns],
  );
  const columnFilteringExtensions = useMemo(
    () =>
      columns.map((column) => ({
        columnName: column.name,
        filteringEnabled: column.filteringEnabled,
        predicate: column.filteringPredicate,
      })),
    [columns],
  );
  const columnsWithoutFilters = useMemo(
    () => columns.filter((column) => !column.filteringEnabled).map((column) => column.name),
    [columns],
  );

  function onExpandedRowsChange(newExpandedRows: (string | number)[]) {
    for (const newlyCollapsedRow of difference(expandedRows, newExpandedRows)) {
      dispatch(collapseRows([newlyCollapsedRow.toString()]));
    }

    for (const newlyExpandedRow of difference(newExpandedRows, expandedRows)) {
      const newlyExpandedRowString = newlyExpandedRow.toString();
      if (isTruckloadRow(newlyExpandedRowString)) {
        dispatch(loadComponentsForTruckload(getTruckloadGuidFromRowId(newlyExpandedRowString)));
      }
      dispatch(expandRows([newlyExpandedRowString]));
    }
  }

  function onCheckedRowsChange(newCheckedRows: (string | number)[]) {
    for (const uncheckedRowStringOrNumber of difference(checkedRows, newCheckedRows)) {
      const uncheckedRowId = uncheckedRowStringOrNumber.toString();

      if (rowSelectionDisabled(rowsKeyedByRowId[uncheckedRowId])) continue;

      const descendantRowIds = getDescendantRowIds(uncheckedRowId);
      dispatch(uncheckRows([uncheckedRowId].concat(descendantRowIds)));
    }

    for (const newlyCheckedRowStringOrNumber of difference(newCheckedRows, checkedRows)) {
      const newlyCheckedRowId = newlyCheckedRowStringOrNumber.toString();

      if (rowSelectionDisabled(rowsKeyedByRowId[newlyCheckedRowId])) continue;

      const descendantRowIds = getDescendantRowIds(newlyCheckedRowId);
      dispatch(checkRows([newlyCheckedRowId].concat(descendantRowIds)));
    }
  }

  return (
    <Paper sx={{ flex: 2, ...expandToFillParentStyles.containerStyleProps }}>
      <Grid rows={rows} columns={columns} getRowId={(row) => row.rowId} rootComponent={GridRoot}>
        <TreeDataState expandedRowIds={expandedRows} onExpandedRowIdsChange={onExpandedRowsChange} />

        <SortingState
          defaultSorting={[
            { columnName: 'shipDate', direction: 'asc' },
            { columnName: 'name', direction: 'asc' },
          ]}
          columnExtensions={columnSortingExtensions}
        />
        <IntegratedSorting columnExtensions={columnSortingExtensions} />

        {/* It is important that this is defined before <SelectionState /> and <IntegratedSelection /> so that select
        all takes into account filtering.*/}
        <FilteringState columnExtensions={columnFilteringExtensions} />
        <IntegratedFiltering columnExtensions={columnFilteringExtensions} />

        <SelectionState selection={castDraft(checkedRows)} onSelectionChange={onCheckedRowsChange} />
        <IntegratedSelection />

        <CustomTreeData
          getChildRows={(currentRow, rootRows) => {
            if (!currentRow) {
              return rootRows.filter((row) => row.type === 'truckload');
            }
            if (currentRow.type === 'truckload') {
              const childRows = naturalSortByProperty(
                rootRows.filter((row) =>
                  row.type === 'productionGroup' ? row.truckloadRowId === currentRow.rowId : false,
                ),
                (productionGroupRowId) => productionGroupRowId.name,
              );
              return childRows.length > 0 ? childRows : null;
            }
            if (currentRow.type === 'productionGroup') {
              const childRows = naturalSortByProperty(
                rootRows.filter((row) =>
                  row.type === 'bundle' ? row.productionGroupRowId === currentRow.rowId : false,
                ),
                (bundleRow) => bundleRow.name,
              );
              return childRows.length > 0 ? childRows : null;
            }
            if (currentRow.type === 'bundle') {
              const childRows = naturalSortByProperty(
                rootRows.filter(
                  (row) => row.type === 'groupedProductionComponentInstance' && row.bundleGuid === currentRow.guid,
                ),
                (groupedProductionComponentInstanceRow) => groupedProductionComponentInstanceRow.name,
              );
              return childRows.length > 0 ? childRows : null;
            }
            return null;
          }}
        />

        <VirtualTable />
        <TableColumnResizing
          columnWidths={
            userColumnWidths ?? columns.map((column) => ({ columnName: column.name, width: column.defaultWidth }))
          }
          onColumnWidthsChange={(newUserColumnWidths) => void dispatch(setUserColumnWidths(newUserColumnWidths))}
        />
        <TableHeaderRow showSortingControls />
        <TableFilterRow
          cellComponent={(props) =>
            columnsWithoutFilters.includes(props.column.name) ? <TableCell /> : <TableFilterRow.Cell {...props} />
          }
        />
        <Toolbar />
        <TruckloadGridToolbarPlugin />
        <TableColumnVisibility />
        <ColumnChooser />
        <TableTreeColumn
          for="name"
          showSelectionControls
          cellComponent={(props) => (
            <CustomTreeCell
              rowSelectionDisabled={rowSelectionDisabled}
              rowSelectionHidden={(row) => row.type !== 'truckload'}
              {...props}
            />
          )}
        />
        <TableSelection selectByRowClick showSelectionColumn={false} />
      </Grid>
    </Paper>
  );
}
